<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import {
  getAdminGoogleSettings,
  saveAdminGoogleSettings,
  type AdminGoogleSettings
} from "../admin-api";

const loading = ref(false);
const saving = ref(false);
const secretPreview = ref("未配置");
const form = reactive({
  enabled: false,
  clientId: "",
  clientSecret: ""
});

onMounted(loadSettings);

async function loadSettings() {
  loading.value = true;
  try {
    applySettings(await getAdminGoogleSettings());
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "Google 登录配置加载失败。");
  } finally {
    loading.value = false;
  }
}

async function submitSettings() {
  saving.value = true;
  try {
    const response = await saveAdminGoogleSettings({
      enabled: form.enabled,
      clientId: form.clientId,
      clientSecret: form.clientSecret || undefined
    });
    message.success(response.message);
    form.clientSecret = "";
    await loadSettings();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "Google 登录配置保存失败。");
  } finally {
    saving.value = false;
  }
}

function applySettings(settings: AdminGoogleSettings) {
  form.enabled = settings.enabled ?? false;
  form.clientId = settings.clientId ?? "";
  form.clientSecret = "";
  secretPreview.value = settings.clientSecret?.hasValue ? settings.clientSecret.preview : "未配置";
}
</script>

<template>
  <a-card class="admin-settings-card" :loading="loading">
    <template #title>Google 登录配置</template>
    <template #extra>
      <a-tag :color="form.enabled ? 'green' : 'red'">{{ form.enabled ? "已启用" : "未启用" }}</a-tag>
    </template>

    <a-alert
      class="admin-form-alert"
      type="info"
      show-icon
      message="国际版 .net 域名显示 Google 登录入口。这里维护 OAuth Client ID / Client Secret，留空的密钥字段会保留原值。"
    />

    <a-form layout="vertical" :model="form" @finish="submitSettings">
      <a-form-item>
        <a-switch v-model:checked="form.enabled" />
        <span style="margin-left: 10px">启用 Google 登录</span>
      </a-form-item>

      <a-form-item label="Google OAuth Client ID">
        <a-input v-model:value="form.clientId" placeholder="xxxxxxxx.apps.googleusercontent.com" />
      </a-form-item>

      <a-form-item label="Google OAuth Client Secret">
        <a-input-password v-model:value="form.clientSecret" :placeholder="`留空不修改，当前：${secretPreview}`" />
      </a-form-item>

      <a-button type="primary" html-type="submit" :loading="saving">保存 Google 配置</a-button>
    </a-form>
  </a-card>
</template>
